var _class, _descriptor, _token, _identifier;
function _initializerDefineProperty(e, i, r, l) { r && Object.defineProperty(e, i, { enumerable: r.enumerable, configurable: r.configurable, writable: r.writable, value: r.initializer ? r.initializer.call(l) : void 0 }); }
function _classPrivateFieldLooseBase(e, t) { if (!{}.hasOwnProperty.call(e, t)) throw new TypeError("attempted to use private field on non-instance"); return e; }
var id = 0;
function _classPrivateFieldLooseKey(e) { return "__private_" + id++ + "_" + e; }
function _applyDecoratedDescriptor(i, e, r, n, l) { var a = {}; return Object.keys(n).forEach(function (i) { a[i] = n[i]; }), a.enumerable = !!a.enumerable, a.configurable = !!a.configurable, ("value" in a || a.initializer) && (a.writable = !0), a = r.slice().reverse().reduce(function (r, n) { return n(i, e, r) || r; }, a), l && void 0 !== a.initializer && (a.value = a.initializer ? a.initializer.call(l) : void 0, a.initializer = void 0), void 0 === a.initializer ? (Object.defineProperty(i, e, a), null) : a; }
function _initializerWarningHelper(r, e) { throw Error("Decorating class property failed. Please ensure that transform-class-properties is enabled and runs after the decorators transform."); }
import { dependentKeyCompat } from '@ember/object/compat';
import { tracked } from '@glimmer/tracking';
import { cached } from "ember-cached-decorator-polyfill";
import { resolve } from 'rsvp';
import { unsubscribe } from '../record-notification-manager';
import { internalModelFactoryFor } from '../store/internal-model-factory';
import Reference from './reference';
/**
  @module @ember-data/store
*/

/**
   A `RecordReference` is a low-level API that allows users and
   addon authors to perform meta-operations on a record.

   @class RecordReference
   @public
   @extends Reference
*/
let RecordReference = (_class = (_token = /*#__PURE__*/_classPrivateFieldLooseKey("token"), _identifier = /*#__PURE__*/_classPrivateFieldLooseKey("identifier"), class RecordReference extends Reference {
  constructor(store, identifier) {
    super(store, identifier);
    // unsubscribe token given to us by the notification manager
    Object.defineProperty(this, _token, {
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, _identifier, {
      writable: true,
      value: void 0
    });
    _initializerDefineProperty(this, "_ref", _descriptor, this);
    this.store = store;
    _classPrivateFieldLooseBase(this, _identifier)[_identifier] = identifier;
    _classPrivateFieldLooseBase(this, _token)[_token] = store._notificationManager.subscribe(identifier, (_, bucket, notifiedKey) => {
      if (bucket === 'identity' || (bucket === 'attributes' || bucket === 'property') && notifiedKey === 'id') {
        this._ref++;
      }
    });
  }
  destroy() {
    unsubscribe(_classPrivateFieldLooseBase(this, _token)[_token]);
  }
  get type() {
    return this.identifier().type;
  }
  get _id() {
    this._ref; // consume the tracked prop
    let identifier = this.identifier();
    if (identifier) {
      return identifier.id;
    }
    return null;
  }

  /**
     The `id` of the record that this reference refers to.
      Together, the `type` and `id` properties form a composite key for
     the identity map.
      Example
      ```javascript
     let userRef = store.getReference('user', 1);
      userRef.id(); // '1'
     ```
      @method id
    @public
     @return {String} The id of the record.
  */
  id() {
    return this._id;
  }

  /**
     The `identifier` of the record that this reference refers to.
      Together, the `type` and `id` properties form a composite key for
     the identity map.
      Example
      ```javascript
     let userRef = store.getReference('user', 1);
      userRef.identifier(); // '1'
     ```
      @method identifier
    @public
     @return {String} The identifier of the record.
  */
  identifier() {
    return _classPrivateFieldLooseBase(this, _identifier)[_identifier];
  }

  /**
     How the reference will be looked up when it is loaded. Currently
     this always returns `identity` to signify that a record will be
     loaded by its `type` and `id`.
      Example
      ```javascript
     const userRef = store.getReference('user', 1);
      userRef.remoteType(); // 'identity'
     ```
      @method remoteType
     @public
     @return {String} 'identity'
  */
  remoteType() {
    return 'identity';
  }

  /**
    This API allows you to provide a reference with new data. The
    simplest usage of this API is similar to `store.push`: you provide a
    normalized hash of data and the object represented by the reference
    will update.
     If you pass a promise to `push`, Ember Data will not ask the adapter
    for the data if another attempt to fetch it is made in the
    interim. When the promise resolves, the underlying object is updated
    with the new data, and the promise returned by *this function* is resolved
    with that object.
     For example, `recordReference.push(promise)` will be resolved with a
    record.
      Example
      ```javascript
     let userRef = store.getReference('user', 1);
      // provide data for reference
     userRef.push({
       data: {
         id: "1",
         type: "user",
         attributes: {
           username: "@user"
         }
       }
     }).then(function(user) {
       userRef.value() === user;
     });
     ```
     @method push
    @public
    @param objectOrPromise a JSON:API ResourceDocument or a promise resolving to one
    @return a promise for the value (record or relationship)
  */
  push(objectOrPromise) {
    return resolve(objectOrPromise).then(data => {
      return this.store.push(data);
    });
  }

  /**
    If the entity referred to by the reference is already loaded, it is
    present as `reference.value`. Otherwise the value returned by this function
    is `null`.
      Example
      ```javascript
     let userRef = store.getReference('user', 1);
      userRef.value(); // user
     ```
      @method value
    @public
     @return {Model} the record for this RecordReference
  */
  value() {
    if (this.id() !== null) {
      let internalModel = internalModelFactoryFor(this.store).peek(_classPrivateFieldLooseBase(this, _identifier)[_identifier]);
      if (internalModel && internalModel.currentState.isLoaded) {
        return internalModel.getRecord();
      }
    }
    return null;
  }

  /**
     Triggers a fetch for the backing entity based on its `remoteType`
     (see `remoteType` definitions per reference type).
      Example
      ```javascript
     let userRef = store.getReference('user', 1);
      // load user (via store.find)
     userRef.load().then(...)
     ```
      @method load
    @public
     @return {Promise<record>} the record for this RecordReference
  */
  load() {
    const id = this.id();
    if (id !== null) {
      return this.store.findRecord(this.type, id);
    }
    throw new Error(`Unable to fetch record of type ${this.type} without an id`);
  }

  /**
     Reloads the record if it is already loaded. If the record is not
     loaded it will load the record via `store.findRecord`
      Example
      ```javascript
     let userRef = store.getReference('user', 1);
      // or trigger a reload
     userRef.reload().then(...)
     ```
      @method reload
    @public
     @return {Promise<record>} the record for this RecordReference
  */
  reload() {
    const id = this.id();
    if (id !== null) {
      return this.store.findRecord(this.type, id, {
        reload: true
      });
    }
    throw new Error(`Unable to fetch record of type ${this.type} without an id`);
  }
}), _descriptor = _applyDecoratedDescriptor(_class.prototype, "_ref", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return 0;
  }
}), _applyDecoratedDescriptor(_class.prototype, "_id", [cached, dependentKeyCompat], Object.getOwnPropertyDescriptor(_class.prototype, "_id"), _class.prototype), _class);
export { RecordReference as default };